import { useState, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import toast from "react-hot-toast";
import styles from "./styles.module.css";
import Button from "@mui/material/Button";
import SalesContext from "context/SalesContext";
import QuickSaleButtons from "../../utils/QuickSaleButtons/QuickSaleButtons";
import PrinterImg from "images/svg/printer.svg";
import TruckImg from "images/svg/elements.png";
import CartDetailsDialog from "../../popups/CartDetailsDialog/CartDetailsDialog";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import {
  SALE_STATUS,
  KOT_STATUS,
  KOT_STATUS_COLORS,
  ROUTES,
  TABLE_SELECTION_MODE,
  MERGE_LABELS,
  ONLINE_ORDER_SOURCES,
  ONLINE_ORDER_SOURCE_LABELS,
} from "const";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import SaleEditDialog from "../../popups/SaleEditDialog/SaleEditDialog";
import ShopContext from "context/ShopContext";
import SearchContext from "context/SearchContext";
import ShopsHelper from "helpers/ShopsHelper";
import { TerminalContext } from "context/TerminalContext";

const calculateDue = (sale) => Number(sale?.due ?? Number(sale?.payable || 0) - Number(sale?.prepaid || 0));

function ActiveOrdersPage() {
  const { sales, setActiveSale, handleMergeSales, updateSaleKotStatus } = useContext(SalesContext);
  const { selectedShop } = useContext(ShopContext);
  const { searchTerm, setSearchTerm } = useContext(SearchContext);
  const { displayAwaitingDispatchOrders } = useContext(TerminalContext);

  const [selectedSale, setSelectedSale] = useState(null);
  const [cartDetailsOpen, setCartDetailsOpen] = useState(false);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [activeOrders, setActiveOrders] = useState([]);
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [selectedSalesForMerge, setSelectedSalesForMerge] = useState([]);

  const navigate = useNavigate();

  const showOrderStatus = useMemo(() => {
    return ShopsHelper.showOrderStatus(selectedShop);
  }, [selectedShop]);

  useEffect(() => {
    const list = sales.filter((sale) => {
      const isDraftOrPrinted = sale.status === SALE_STATUS.DRAFT || sale.status === SALE_STATUS.PRINTED;
      const isAwaitingDispatch =
        displayAwaitingDispatchOrders &&
        sale.status === SALE_STATUS.PAID &&
        [KOT_STATUS.PENDING, KOT_STATUS.PREPARING, KOT_STATUS.READY].includes(sale.kot_status);

      return (isDraftOrPrinted || isAwaitingDispatch) && sale.status !== SALE_STATUS.VOID;
    });

    list.sort((a, b) => {
      const ta = a?.time ? new Date(a.time).getTime() : (a.id ?? 0);
      const tb = b?.time ? new Date(b.time).getTime() : (b.id ?? 0);
      return tb - ta;
    });

    setActiveOrders(list);
  }, [sales, displayAwaitingDispatchOrders]);

  const openOrderDetails = (sale) => {
    setSelectedSale(sale);
    setCartDetailsOpen(true);
    setSearchTerm("");
  };

  const handlePrintInvoice = (e, sale) => {
    e.stopPropagation();
    setSelectedSale(sale);
    setBillDialogOpen(true);
  };

  const handleDispatch = async (e, sale) => {
    e.stopPropagation();
    await updateSaleKotStatus(sale, KOT_STATUS.DISPATCH);
  };

  const handleTableSelectionMode = (sale) => {
    if (sale) {
      setActiveSale(sale);
      navigate(ROUTES.TABLE_DASHBOARD, { state: { mode: TABLE_SELECTION_MODE.CHANGE_TABLE, sale } });
    }
  };

  const handleTableMergeMode = (sale) => {
    if (sale?.editing_sale_local_id) {
      toast.error("Can't merge editing sale");
      return;
    }
    setIsMergeMode(true);
    setSelectedSalesForMerge([sale]);
    setCartDetailsOpen(false);
  };

  const handleCancelMerge = () => {
    setIsMergeMode(false);
    setSelectedSalesForMerge([]);
  };

  const handleToggleMergeSale = (sale) => {
    if (sale?.editing_sale_local_id) {
      toast.error("Can't merge editing sale");
      return;
    }
    setSelectedSalesForMerge((prev) => {
      const isAlreadyIn = prev.some((s) => s.local_id === sale.local_id);
      if (isAlreadyIn) {
        return prev.filter((s) => s.local_id !== sale.local_id);
      } else {
        return [...prev, sale];
      }
    });
  };

  const handleSaveMergeSales = async () => {
    if (selectedSalesForMerge.length < 2) {
      toast.error("Please select at least 2 sales to merge.");
      return;
    }

    const firstSale = selectedSalesForMerge[0];
    const targetTable = firstSale.table;

    const success = await handleMergeSales(selectedSalesForMerge, targetTable);
    if (success) {
      toast.success("Sales merged successfully.");
      setIsMergeMode(false);
      setSelectedSalesForMerge([]);
    } else {
      toast.error("Merge failed.");
    }
  };

  const handleCardClick = (sale) => {
    if (isMergeMode) {
      handleToggleMergeSale(sale);
    } else {
      openOrderDetails(sale);
    }
  };

  const getOnlineOrderSourceInfo = (sale) => {
    const source = sale?.online_order_source;
    if (!source) return null;

    const normalized = String(source).toLowerCase().trim();
    switch (normalized) {
      case ONLINE_ORDER_SOURCES.SANOFT_CART:
        return {
          label: ONLINE_ORDER_SOURCE_LABELS[ONLINE_ORDER_SOURCES.SANOFT_CART],
          className: styles.sourceSanoft,
        };
      case ONLINE_ORDER_SOURCES.SWIGGY:
        return {
          label: ONLINE_ORDER_SOURCE_LABELS[ONLINE_ORDER_SOURCES.SWIGGY],
          className: styles.sourceSwiggy,
        };
      case ONLINE_ORDER_SOURCES.ZOMATO:
        return {
          label: ONLINE_ORDER_SOURCE_LABELS[ONLINE_ORDER_SOURCES.ZOMATO],
          className: styles.sourceZomato,
        };
      case ONLINE_ORDER_SOURCES.EXTERNAL_API:
        return {
          label: ONLINE_ORDER_SOURCE_LABELS[ONLINE_ORDER_SOURCES.EXTERNAL_API],
          className: styles.sourceExternal,
        };
      default:
        return { label: normalized.toUpperCase(), className: styles.sourceDefault };
    }
  };

  const filteredOrders = useMemo(() => {
    if (!searchTerm) return activeOrders;

    const term = searchTerm.toLowerCase();
    return activeOrders.filter((sale) => {
      const tableName = (sale?.table?.name || "").toLowerCase();
      const chairName = (sale?.table?.chair?.name || "").toLowerCase();
      const saleLocalId = String(sale?.local_id || "").toLowerCase();

      return (
        tableName.includes(term) ||
        chairName.includes(term) ||
        saleLocalId.includes(term) ||
        (sale.online_order_source || "").includes(term)
      );
    });
  }, [activeOrders, searchTerm]);

  return (
    <Grid className={styles.pageWrap} item xs={12} md={4}>
      <Grid item xs={12} md={4}>
        {/* Cards grid */}
        <Grid item xs={12} className={styles.contentWrapper}>
          <Grid container spacing={1.25} className={styles.gridArea}>
            {/* Quick action buttons */}
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <QuickSaleButtons height="288px" />
            </Grid>

            {/* Active Orders */}
            {filteredOrders.map((sale, idx) => {
              const key = sale.id ?? sale._id ?? idx;
              const items = sale?.items ?? [];

              const isSelected = selectedSalesForMerge.some((s) => s.local_id === sale.local_id);
              const isAwaitingDispatchCard =
                displayAwaitingDispatchOrders &&
                sale.status === SALE_STATUS.PAID &&
                [KOT_STATUS.PENDING, KOT_STATUS.PREPARING, KOT_STATUS.READY].includes(sale.kot_status);
              const sourceInfo = getOnlineOrderSourceInfo(sale);

              return (
                <Grid key={key} item lg={3} md={4} sm={6} xs={12}>
                  <div
                    className={`${styles.card} ${isSelected ? styles.selectedCard : ""} ${isAwaitingDispatchCard ? styles.awaitingDispatchCard : ""}`}
                    onClick={() => handleCardClick(sale)}
                  >
                    {isMergeMode && !sale?.editing_sale_local_id && (
                      <div className={styles.selectionOverlay}>
                        <input type="checkbox" checked={isSelected} readOnly className={styles.checkbox} />
                      </div>
                    )}
                    <div className={`${styles.cardHeader} ${sale?.editing_sale_local_id ? styles.editCartHeader : ""}`}>
                      <div className={styles.cartNoGroup}>
                        {sale?.editing_sale_local_id && (
                          <span className={styles.editCartBadge}>Edit Sale: {sale?.original_sale_bill_no}</span>
                        )}
                        <span className={styles.cartNo}>Cart No: {sale?.local_id}</span>
                        {sourceInfo && (
                          <span className={`${styles.sourceBadge} ${sourceInfo.className}`}>{sourceInfo.label}</span>
                        )}
                      </div>
                      <span className={styles.viewMore}>
                        Click to view more <ArrowForwardRoundedIcon className={styles.forwardIcon} />
                      </span>
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.metaRow}>
                        <div className={styles.metaRowLine}>
                          <div className={styles.metaCol}>
                            <div className={styles.metaLabel}>Table</div>
                            <div className={styles.colon}>:</div>
                            <div className={styles.metaValue}>
                              {sale?.table?.name
                                ? `${sale.table.name}${sale.table.chair?.name ? ` (${sale.table.chair.name})` : ""}`
                                : "-"}
                            </div>
                          </div>

                          <div className={styles.metaCol}>
                            <div className={styles.metaLabel}>Created</div>
                            <div className={styles.colon}>:</div>
                            <div className={styles.metaValue}>{DateTimeHelpers.getLocalTime(sale?.time)}</div>
                          </div>
                        </div>

                        <div className={styles.metaRowLine}>
                          <div className={styles.metaCol}>
                            <div className={styles.metaLabel}>Items</div>
                            <div className={styles.colon}>:</div>
                            <div className={styles.metaValue}>{sale?.items?.length ?? 0}</div>
                          </div>

                          {showOrderStatus ? (
                            <div className={styles.metaCol}>
                              <div className={styles.metaLabel}>Kot Status</div>
                              <div className={styles.colon}>:</div>
                              <div className={styles.metaValue}>
                                <span
                                  className={styles.statusBadge}
                                  style={{
                                    background:
                                      KOT_STATUS_COLORS[sale.kot_status] || KOT_STATUS_COLORS[KOT_STATUS.PENDING],
                                  }}
                                >
                                  {sale.kot_status || KOT_STATUS.PENDING}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.metaCol} />
                          )}
                        </div>

                        {(sale?.waiter || sale?.order_type) && (
                          <div className={styles.metaRowLine}>
                            {sale?.waiter ? (
                              <div className={styles.metaCol}>
                                <div className={styles.metaLabel}>Waiter</div>
                                <div className={styles.colon}>:</div>
                                <div className={styles.metaValue}>{sale?.waiter?.name}</div>
                              </div>
                            ) : (
                              <div className={styles.metaCol} />
                            )}

                            {sale?.order_type ? (
                              <div className={styles.metaCol}>
                                <div className={styles.metaLabel}>Order Type</div>
                                <div className={styles.colon}>:</div>
                                <div className={styles.metaValue}>{sale?.order_type}</div>
                              </div>
                            ) : (
                              <div className={styles.metaCol} />
                            )}
                          </div>
                        )}
                      </div>

                      <div className={styles.itemsTable}>
                        <div className={styles.itemsHeader}>
                          <div className={styles.colNo}>No</div>
                          <div className={styles.colItem}>Items</div>
                          <div className={styles.colRate}>Rate</div>
                          <div className={styles.colQty}>Qty</div>
                        </div>

                        <div className={styles.itemsBody}>
                          {items.length === 0 ? (
                            <div className={styles.emptyRow}>No items</div>
                          ) : (
                            items.map((it, i) => (
                              <div className={styles.itemRow} key={it.id ?? it._id ?? i}>
                                <div className={styles.colNo}>{i + 1}</div>
                                <div className={styles.colItem} title={it.name}>
                                  {it.name} {it.option?.label ? ` - ${it.option.label}` : ""}
                                </div>
                                <div className={styles.colRate}>
                                  {NumberFormatHelper.amountRoundOff(it.rateAndTax ?? 0)}
                                </div>
                                <div className={styles.colQty}>{it.count ?? it.qty ?? 1}</div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardFooter}>
                      <div className={styles.dueAmount}>
                        <div className={styles.dueAmountLine}>
                          Due Amount:
                          <div className={styles.amount}>
                            <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                            {NumberFormatHelper.amountRoundOff(calculateDue(sale))}
                          </div>
                        </div>
                        <div>
                          Sale Status: <span className={styles.saleStatusBadge}>{sale.status}</span>
                        </div>
                      </div>

                      <div className={styles.actionButtons}>
                        {sale.status !== SALE_STATUS.PAID && (
                          <Button
                            fullWidth
                            className={styles.printButton}
                            startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
                            onClick={(e) => handlePrintInvoice(e, sale)}
                            disabled={!sale?.items?.length || isMergeMode}
                          >
                            Print Invoice
                          </Button>
                        )}
                        {displayAwaitingDispatchOrders && sale.kot_status === KOT_STATUS.READY && (
                          <Button
                            fullWidth
                            className={styles.dispatchButton}
                            startIcon={<img src={TruckImg} alt="Dispatch" className={styles.iconSmall} />}
                            onClick={(e) => handleDispatch(e, sale)}
                            disabled={!sale?.items?.length || isMergeMode}
                          >
                            Dispatch
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
      {cartDetailsOpen && (
        <CartDetailsDialog
          open={Boolean(selectedSale)}
          onClose={() => setCartDetailsOpen(false)}
          saleId={selectedSale?.local_id}
          onTableSelectionMode={handleTableSelectionMode}
          onTableMergeMode={handleTableMergeMode}
          mergeLabel={MERGE_LABELS.SALES}
        />
      )}
      {billDialogOpen && (
        <SaleEditDialog handleClose={() => setBillDialogOpen(false)} saleLocalId={selectedSale?.local_id} />
      )}

      {isMergeMode && (
        <div className={styles.selectionBar}>
          <div className={styles.selectionInfo}>
            <span className={styles.instructionText}>Select orders to merge</span>
          </div>
          <div className={styles.selectedOrders}>
            <span className={styles.selectedText}>
              {selectedSalesForMerge
                .map((sale) => {
                  const saleId = sale?.local_id;
                  return `Sale ${saleId}`;
                })
                .join(" | ")}
            </span>
          </div>
          <div className={styles.selectionActions}>
            <Button className={styles.cancelBtn} onClick={handleCancelMerge}>
              Cancel
            </Button>
            <Button
              className={styles.saveBtn}
              onClick={handleSaveMergeSales}
              disabled={selectedSalesForMerge.length < 2}
            >
              Save & Proceed
            </Button>
          </div>
        </div>
      )}
    </Grid>
  );
}

export default ActiveOrdersPage;
